import { ReactNode, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { AppAction, AppActionContext, AppState } from './AppState.ts'
import { Camera } from './Camera.ts'
import { Decoder } from './Coding.ts'
import { Hit } from './Entity.ts'
import { FloorView } from './Floor.tsx'
import { decodeMeshViewSettingsFrom, MeshView, MeshViewSettings, MeshViewSettings_fallback } from './MeshView.tsx'
import { MouseHandler } from './MouseHandler.tsx'
import { SkeletonView } from './SkeletonView.tsx'
import { WorldAxesView } from './WorldAxes.tsx'
import { DrawProps, GatherDrawCalls, useDrawCalls } from './hooks/useDrawCall.tsx'
import { GLContext } from './hooks/useGL.ts'

export interface ViewportSettings {
  readonly camera: Camera
  readonly meshView: MeshViewSettings
  readonly worldAxesEnabled: boolean
  readonly bonesEnabled: boolean
  readonly floorEnabled: boolean
}

export function decodeViewportSettingsFrom(decoder: Decoder): ViewportSettings {
  const container = decoder.keyedDecoder()
  return {
    camera: container.decodeInstance('camera', Camera.decodeFrom),
    meshView: container.decodeInstance('meshView', decodeMeshViewSettingsFrom),
    worldAxesEnabled: container.decodeBoolean('worldAxesEnabled', false),
    bonesEnabled: container.decodeBoolean('bonesEnabled', true),
    floorEnabled: container.decodeBoolean('floorEnabled', true),
  }
}

export const ViewportSettings_fallback: ViewportSettings = {
  camera: Camera.fallback,
  meshView: MeshViewSettings_fallback,
  worldAxesEnabled: false,
  bonesEnabled: true,
  floorEnabled: true,
}

export function Viewport(props: {
  appState: AppState
}): ReactNode {
  const { appState } = props
  const settings = appState.settings.viewport

  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null)
  const [gl, fbo] = useCanvasForGL(canvas)

  return <canvas
    ref={setCanvas}
    style={{
      // I don't know why, but leaving overflow unset makes the canvas only grow vertically and not shrink (in Chrome at least).
      overflow: 'auto',
      flexGrow: 1,
      imageRendering: 'pixelated',
    }}
  >
    {gl && fbo && <>
      <MouseHandler
        state={appState.mouse}
        element={gl.canvas as HTMLCanvasElement}
      />

      <GatherDrawCalls>
        <GLContext.Provider value={gl}>
          <FloorView floor={appState.floor} enabled={settings.floorEnabled} />
          <WorldAxesView worldAxes={appState.worldAxes} enabled={settings.worldAxesEnabled} />
          <MeshView
            posedMannequin={appState.posedMannequin}
            settings={settings.meshView}
            selectedBone={appState.selectedBone}
          />
          <SkeletonView skeleton={appState.posedSkeleton} enabled={settings.bonesEnabled} selectedBone={appState.selectedBone} />
        </GLContext.Provider>

        <Renderer
          gl={gl}
          fbo={fbo}
          camera={settings.camera}
          hit={appState.mouse.hit}
        />
      </GatherDrawCalls>
    </>}
  </canvas>
}

function Renderer(props: {
  hit: Hit
  gl: WebGL2RenderingContext
  fbo: WebGLFramebuffer
  camera: Camera
}): ReactNode {
  const { gl, fbo, camera, hit } = props
  const drawCalls = useDrawCalls()

  useEffect(() => {
    if (!gl.isFramebuffer(fbo)) {
      return
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)

    const clipFromWorld = camera.clipFromView.times(camera.viewFromWorld)
    const drawProps: DrawProps = { gl, clipFromWorld, mouseHit: hit, }
    for (const drawCall of drawCalls) {
      drawCall(drawProps)
    }

    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, fbo)
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null)
    gl.blitFramebuffer(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight, 0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight, gl.COLOR_BUFFER_BIT, gl.NEAREST)
  }, [gl, fbo, camera, drawCalls, hit])

  return null
}

function useCanvasForGL(canvas: HTMLCanvasElement | null): [WebGL2RenderingContext | null, WebGLFramebuffer | null] {
  const gl = useMemo(() => {
    return canvas?.getContext('webgl2', {
      /**
       * I want to use a floating-point depth buffer (for reverse-Z) and standard WebGL2 doesn't offer a way to request that on the default framebuffer.
       *
       * Chrome has `drawingBufferStorage` which might allow me to change the default framebuffer's depth buffer, but Safari and Firefox don't support that function. So I draw to a framebuffer object instead, and then blit to the default framebuffer.
       *
       * By default, `getContext` creates a default framebuffer with antialiasing enabled, which sets `SAMPLE_BUFFERS` to 1. Unfortunately, `blitFramebuffer` is required to fail (`INVALID_OPERATION`) if the draw framebuffer's `SAMPLE_BUFFERS > 0`. (OpenGL ES 3.0.6 §4.3.3 last sentence.)
       *
       * So here I set `antialias` to false to get a default framebuffer with `SAMPLE_BUFFERS = 0`. I will set up the renderbuffers for multisampling to restore antialiasing.
       */
      antialias: false
    }) ?? null
  }, [canvas])

  const sendAppAction = useContext(AppActionContext)

  const [fbo, setFbo] = useState<WebGLFramebuffer | null>(null)

  useEffect(() => {
    if (!gl) {
      return
    }

    configureWebGLLint(gl)
    gl.clearColor(0.47, 0.76, 0.99, 1)
    configureHiddenFaceSettings(gl)
  }, [gl, setFbo, sendAppAction])

  const [resizeCount, setResizeCount] = useState(0)

  useLayoutEffect(() => {
    if (!canvas || !gl) {
      return () => { }
    }
    const observer = new ResizeObserver((_entries, _observer) => {
      setResizeCount((count) => count + 1)
    })

    observer.observe(canvas)

    return () => observer.disconnect()
  }, [canvas])

  useLayoutEffect(() => {
    if (!gl) {
      return
    }
    configureCanvasSize(gl, sendAppAction)
    return configureFramebuffer(gl, setFbo)
  }, [canvas, gl, resizeCount])

  return [gl, fbo]
}

function configureWebGLLint(gl: WebGL2RenderingContext) {
  const ext_debug = gl.getExtension('GMAN_debug_helper')
  if (ext_debug) {
    if (import.meta.env.DEV) {
      ext_debug.setConfiguration({
        // Without this, webgl-lint crashes after 1000 draw calls.
        maxDrawCalls: 0,
      })
    } else {
      ext_debug.disable()
    }
  }
}

function configureCanvasSize(gl: WebGL2RenderingContext, sendAppAction: (action: AppAction) => void) {
  const canvas = gl.canvas
  if (!(canvas instanceof HTMLCanvasElement)) {
    return
  }

  // Make the canvas's logical size match its visual size, so that it is drawn at full resolution and with the correct aspect ratio.
  // https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/By_example/Canvas_size_and_WebGL
  const scale = window.devicePixelRatio
  canvas.width = canvas.clientWidth * scale
  canvas.height = canvas.clientHeight * scale

  sendAppAction({ type: 'setAspectRatio', ratio: gl.drawingBufferWidth / gl.drawingBufferHeight })
  gl.viewport(0, 0, canvas.width, canvas.height)
}

function configureHiddenFaceSettings(gl: WebGL2RenderingContext) {
  gl.enable(gl.CULL_FACE)
  gl.enable(gl.DEPTH_TEST)
}

function configureFramebuffer(
  gl: WebGL2RenderingContext,
  setFbo: (fbo: WebGLFramebuffer) => void
): () => void {
  // Reverse-Z depth buffer setup
  // https://nlguillemot.wordpress.com/2016/12/07/reversed-z-in-opengl/
  // https://registry.khronos.org/webgl/extensions/EXT_clip_control/
  // https://developer.nvidia.com/content/depth-precision-visualized

  // I use a perspective matrix that produces NDC z values of 1 at the near plane to 0 at infinity. This means I need to use gl.GREATER instead of gl.LESS for depth testing, and I need to clear the depth buffer to 0 instead of 1.
  gl.depthFunc(gl.GREATER)
  gl.clearDepth(0)
  // If EXT_clip_control is available, I use it to tell GL all my depth values will be in the 0 to 1 range instead of its default -1 to 1 range, so it can store values with greater precision.
  const ext_cc = gl.getExtension('EXT_clip_control')
  ext_cc?.clipControlEXT(ext_cc?.LOWER_LEFT_EXT, ext_cc?.ZERO_TO_ONE_EXT)

  // Since I'm drawing to an FBO, I have to manually enable multisampling when I set up the renderbuffers.
  const samples = gl.getParameter(gl.MAX_SAMPLES)

  const rbColor = gl.createRenderbuffer()
  gl.bindRenderbuffer(gl.RENDERBUFFER, rbColor)
  // RGBA8 + multisampling works. SRGB8_ALPHA8 + multisampling causes blitFramebuffer to fail in my testing in Chrome.
  gl.renderbufferStorageMultisample(gl.RENDERBUFFER, samples, gl.RGBA8, gl.drawingBufferWidth, gl.drawingBufferHeight)

  const rbDepth = gl.createRenderbuffer()
  gl.bindRenderbuffer(gl.RENDERBUFFER, rbDepth)
  gl.renderbufferStorageMultisample(gl.RENDERBUFFER, samples, gl.DEPTH_COMPONENT32F, gl.drawingBufferWidth, gl.drawingBufferHeight)

  gl.bindRenderbuffer(gl.RENDERBUFFER, null)

  const fbo = gl.createFramebuffer()
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
  gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, rbColor)
  gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, rbDepth)
  gl.bindFramebuffer(gl.FRAMEBUFFER, null)
  setFbo(fbo)

  return () => {
    gl.deleteFramebuffer(fbo)
    gl.deleteRenderbuffer(rbDepth)
    gl.deleteRenderbuffer(rbColor)
  }
}
